import React from 'react'
import PropTypes from 'prop-types'
import {
    useShopperBasketsMutation,
    useShopperOrdersMutation,
    useShippingMethodsForShipment
} from '@salesforce/commerce-sdk-react'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {PayPalScriptProvider, PayPalButtons} from '@paypal/react-paypal-js'
import {paypalPaymentSubmit} from './paypal-payment'
import {useCurrentBasket} from '../../../hooks/use-current-basket'
import {useSiteCustomPreferences} from '../../../hooks/use-site-custom-preferences'

const PayPalContainer = ({props}) => {
    const {
        basket,
        isExpressPaypal,
        onBillingSubmit,
        setShowCC,
        setPaypalButtonClicked,
        setPayPalPurchaseStatus,
        onPayPalOpen
    } = props
    const navigate = useNavigation()
    const {mutateAsync: updateBasket} = useShopperBasketsMutation('updateBasket')
    const {mutateAsync: addPaymentInstrumentToBasket} = useShopperBasketsMutation(
        'addPaymentInstrumentToBasket'
    )
    const {mutateAsync: removePaymentInstrumentFromBasket} = useShopperBasketsMutation(
        'removePaymentInstrumentFromBasket'
    )
    const {mutateAsync: updateCustomerForBasket} =
        useShopperBasketsMutation('updateCustomerForBasket')
    const {mutateAsync: updateShippingAddressForShipment} = useShopperBasketsMutation(
        'updateShippingAddressForShipment'
    )
    const {mutateAsync: updateBillingAddressForBasket} = useShopperBasketsMutation(
        'updateBillingAddressForBasket'
    )
    const {mutateAsync: createOrder} = useShopperOrdersMutation('createOrder')
    const updateShippingMethodForShipmentsMutation = useShopperBasketsMutation(
        'updateShippingMethodForShipment'
    )

    useShippingMethodsForShipment(
        {
            parameters: {
                basketId: basket?.basketId,
                shipmentId: 'me'
            }
        },
        {
            // only fetch if basket is has no shipping method in the first shipment
            enabled:
                !!basket?.basketId &&
                basket?.shipments?.length > 0 &&
                !basket?.shipments[0]?.shippingMethod,
            onSuccess: (data) => {
                updateShippingMethodForShipmentsMutation.mutate({
                    parameters: {
                        basketId: basket?.basketId,
                        shipmentId: 'me'
                    },
                    body: {
                        id: data.defaultShippingMethodId
                    }
                })
            }
        }
    )

    return (
        <PayPalButtons
            fundingSource="paypal"
            onClick={() => {
                onPayPalOpen && onPayPalOpen()
            }}
            onError={() => {
                setPaypalButtonClicked && setPaypalButtonClicked(false)
            }}
            onCancel={() => {
                setPaypalButtonClicked && setPaypalButtonClicked(false)
            }}
            createOrder={async (data, actions) => {
                setPaypalButtonClicked && setPaypalButtonClicked(true)
                return actions.order.create({
                    purchase_units: [
                        {
                            amount: {
                                currency_code: basket?.currency,
                                value: basket?.orderTotal ?? basket?.productSubTotal
                            }
                        }
                    ]
                })
            }}
            onApprove={async (data, actions) => {
                const resp = await actions.order.authorize()
                paypalPaymentSubmit({
                    resp,
                    basket,
                    updateBasket,
                    addPaymentInstrumentToBasket,
                    removePaymentInstrumentFromBasket,
                    updateShippingAddressForShipment,
                    updateBillingAddressForBasket,
                    updateCustomerForBasket,
                    createOrder,
                    onBillingSubmit,
                    setShowCC,
                    isExpressPaypal,
                    setPaypalButtonClicked,
                    navigate,
                    setPayPalPurchaseStatus
                })
            }}
        />
    )
}

const PayPal = (props) => {
    const {data: basket} = useCurrentBasket()
    const {getcustomPreferencevalue} = useSiteCustomPreferences()
    const paypalClientID = getcustomPreferencevalue('pwaPayPalClientId')

    const initialOptions = {
        'client-id': paypalClientID,
        intent: 'authorize',
        currency: basket?.currency
    }

    if (!paypalClientID || !initialOptions['client-id']) {
        return
    }
    const properties = {basket, ...props}

    return (
        <PayPalScriptProvider options={initialOptions}>
            <PayPalContainer props={properties} />
        </PayPalScriptProvider>
    )
}

PayPalContainer.propTypes = {
    props: PropTypes.object,
    basket: PropTypes.object,
    onBillingSubmit: PropTypes.func,
    setShowCC: PropTypes.func,
    isExpressPaypal: PropTypes.bool,
    setPaypalButtonClicked: PropTypes.func,
    setPayPalPurchaseStatus: PropTypes.func,
    onPayPalOpen: PropTypes.func
}

export default PayPal
